/**
 * Utility functions for tile management
 */

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Wait for the next animation frame
 */
export function waitNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

/**
 * Wait for multiple animation frames
 */
export function waitFrames(count: number): Promise<void> {
  let remaining = count;
  return new Promise((resolve) => {
    const tick = () => {
      remaining--;
      if (remaining <= 0) {
        resolve();
      } else {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Create a debounced version of a function
 */
export function debounce<T extends unknown[]>(
  func: (...args: T) => void,
  delay: number
): (...args: T) => void {
  let timeoutId: number | undefined;
  
  return (...args: T) => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = window.setTimeout(() => {
      func(...args);
      timeoutId = undefined;
    }, delay);
  };
}

/**
 * Create a throttled version of a function
 */
export function throttle<T extends unknown[]>(
  func: (...args: T) => void,
  delay: number
): (...args: T) => void {
  let lastCall = 0;
  
  return (...args: T) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Check if a value is defined (not null or undefined)
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Safe array access that returns undefined for out-of-bounds indices
 */
export function safeArrayGet<T>(array: T[], index: number): T | undefined {
  return index >= 0 && index < array.length ? array[index] : undefined;
}

/**
 * Create a promise that resolves after a specified delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Create a promise that resolves with a timeout fallback
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutValue?: T
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve, reject) => {
      setTimeout(() => {
        if (timeoutValue !== undefined) {
          resolve(timeoutValue);
        } else {
          reject(new Error(`Operation timed out after ${timeoutMs}ms`));
        }
      }, timeoutMs);
    }),
  ]);
}

/**
 * Batch process an array with a specified batch size and delay between batches
 */
export async function processBatches<T>(
  items: T[],
  batchSize: number,
  processor: (batch: T[]) => Promise<void>,
  delayBetweenBatches = 0
): Promise<void> {
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    await processor(batch);
    
    if (delayBetweenBatches > 0 && i + batchSize < items.length) {
      await delay(delayBetweenBatches);
    }
  }
}

/**
 * Create a Map with default value factory
 */
export class DefaultMap<K, V> extends Map<K, V> {
  constructor(private readonly defaultFactory: (key: K) => V) {
    super();
  }

  get(key: K): V {
    if (!this.has(key)) {
      this.set(key, this.defaultFactory(key));
    }
    return super.get(key)!;
  }
}

/**
 * Create a cancellable operation
 */
export interface CancellableOperation {
  cancel(): void;
  isCancelled: boolean;
}

export function createCancellableOperation(): CancellableOperation {
  let cancelled = false;
  
  return {
    cancel() {
      cancelled = true;
    },
    get isCancelled() {
      return cancelled;
    },
  };
}

/**
 * Parse a tile key into its components
 */
export interface TileKeyComponents {
  face: string;
  level: number;
  x: number;
  y: number;
}

export function parseTileKey(tileKey: string): TileKeyComponents | null {
  // Expected format: face_Llevel_x_y (e.g., "f_L1_0_0")
  const parts = tileKey.split('_');
  if (parts.length !== 4) {
    return null;
  }
  
  const [face, levelPart, xPart, yPart] = parts;
  
  // Parse level (remove 'L' prefix)
  const level = parseInt(levelPart.substring(1), 10);
  const x = parseInt(xPart, 10);
  const y = parseInt(yPart, 10);
  
  if (isNaN(level) || isNaN(x) || isNaN(y)) {
    return null;
  }
  
  return { face, level, x, y };
}

/**
 * Check if two arrays have the same elements (order doesn't matter)
 */
export function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  
  return sortedA.every((val, index) => val === sortedB[index]);
}

/**
 * Get unique values from an array
 */
export function unique<T>(array: T[]): T[] {
  return [...new Set(array)];
}

/**
 * Group array elements by a key function
 */
export function groupBy<T, K extends string | number>(
  array: T[],
  keyFn: (item: T) => K
): Record<K, T[]> {
  return array.reduce((groups, item) => {
    const key = keyFn(item);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<K, T[]>);
}
